/**
 * WindowPicker — the CONTRACT §3 window, editable, always in IST.
 *
 * Covers all seven types: same_day, yesterday, last_n_days, mtd, mtd_yesterday,
 * from_date, custom.
 * Whatever is picked, the component shows the dates it resolves to *right now*, in
 * IST, because "Last 3 days" is not a thing anyone can check and "13 Sep → 15 Sep IST"
 * is. Resolution here uses the same `resolveWindow` the dispatcher mirrors, so the
 * preview and the real run agree.
 *
 * The guardrail is the point of this component. CONTRACT §3:
 *
 *   "if report_shape == 'detailed' and the resolved window spans > 3 days, the
 *    dispatcher refuses and records status=failed ... A month-to-date detailed report
 *    is ~28M rows and cannot be delivered; do not attempt it."
 *
 * So the console refuses first, in plain words, and offers the one-click fix — the
 * borrower shape, which rolls the same window up to one row per account. Letting an
 * operator queue a run that is guaranteed to fail is the failure mode this exists to
 * prevent.
 *
 *   <WindowPicker
 *     value={reportWindow}
 *     onChange={setReportWindow}
 *     shape={shape}
 *     onShapeChange={setShape}   // enables "Switch to borrower" in the guardrail
 *   />
 *
 * `windowError(window, shape)` is exported so a parent can block Save with the same
 * rule the picker displays — the message the operator reads and the reason the button
 * is disabled must be the same sentence.
 */

import Button from '@/components/Button';
import { Field, Input, Select } from '@/components/Field';
import {
  describeWindow,
  istDate,
  istDateRange,
  istToday,
  pluralise,
  resolveWindow,
} from '@/lib/format';
import { DETAILED_MAX_WINDOW_DAYS, WINDOW_TYPES } from '@/lib/types';
import type { IsoDate, ReportShape, ReportWindow, WindowType } from '@/lib/types';

/** A `last_n_days` bigger than this is a typo, not a request. */
export const MAX_LAST_N_DAYS = 365;

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** True only for a real calendar date in `YYYY-MM-DD` form (rejects 2026-02-31). */
export function isValidIsoDate(value: string | null | undefined): value is IsoDate {
  if (!value) return false;
  const match = DATE_RE.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return false;
  const probe = new Date(Date.UTC(year, month - 1, day));
  return (
    probe.getUTCFullYear() === year &&
    probe.getUTCMonth() === month - 1 &&
    probe.getUTCDate() === day
  );
}

/**
 * A sensible window of the requested type, carrying over any dates the operator had
 * already typed. Switching custom → from_date should not silently discard the start
 * date they just picked.
 */
export function defaultWindowFor(type: WindowType, previous?: ReportWindow): ReportWindow {
  const today = istToday();
  const previousFrom =
    previous && (previous.type === 'from_date' || previous.type === 'custom') ? previous.from : undefined;
  const previousTo = previous && previous.type === 'custom' ? previous.to : undefined;

  switch (type) {
    case 'same_day':
      return { type: 'same_day' };
    case 'yesterday':
      return { type: 'yesterday' };
    case 'last_n_days':
      return { type: 'last_n_days', n: previous && previous.type === 'last_n_days' ? previous.n : 3 };
    case 'mtd':
      return { type: 'mtd' };
    case 'mtd_yesterday':
      return { type: 'mtd_yesterday' };
    case 'from_date':
      return { type: 'from_date', from: previousFrom ?? today };
    case 'custom':
      return { type: 'custom', from: previousFrom ?? today, to: previousTo ?? previousFrom ?? today };
    default:
      return { type: 'yesterday' };
  }
}

/** The §3 refusal, worded for a human who now has to do something else instead. */
export function detailedGuardrailMessage(days: number): string {
  return (
    `A detailed report is one row per call. Over ${days} days that runs to tens of millions of rows — ` +
    `a month-to-date detailed export is around 28 million — so the dispatcher refuses anything past ` +
    `${DETAILED_MAX_WINDOW_DAYS} days and the file would not be deliverable even if it did not. ` +
    `Use the borrower shape: same window, rolled up to one row per account.`
  );
}

/**
 * The single blocking problem with this window, or null. Shape is optional: pass it
 * to include the §3 detailed guardrail, omit it to validate the dates alone.
 */
export function windowError(
  window: ReportWindow,
  shape?: ReportShape,
  now: Date = new Date(),
): string | null {
  const today = istToday(now);

  switch (window.type) {
    case 'last_n_days': {
      if (!Number.isInteger(window.n) || window.n < 1) {
        return 'Enter a whole number of days, 1 or more.';
      }
      if (window.n > MAX_LAST_N_DAYS) {
        return `${MAX_LAST_N_DAYS} days is the ceiling here — anything larger is a typo, not a window.`;
      }
      break;
    }
    case 'from_date': {
      if (!isValidIsoDate(window.from)) return 'Pick a start date.';
      if (window.from > today) {
        return `That start date is in the future — today is ${istDate(today)} IST and no source file exists past it.`;
      }
      break;
    }
    case 'custom': {
      if (!isValidIsoDate(window.from)) return 'Pick a start date.';
      if (!isValidIsoDate(window.to)) return 'Pick an end date.';
      if (window.from > window.to) return 'The start date is after the end date.';
      if (window.to > today) {
        return `That end date is in the future — today is ${istDate(today)} IST and no source file exists past it.`;
      }
      break;
    }
    default:
      break;
  }

  if (shape === 'detailed') {
    const { days } = resolveWindow(window, now);
    if (days > DETAILED_MAX_WINDOW_DAYS) return detailedGuardrailMessage(days);
  }

  return null;
}

export interface WindowPickerProps {
  value: ReportWindow;
  onChange: (next: ReportWindow) => void;
  /** Pass the schedule's shape to arm the §3 guardrail. */
  shape?: ReportShape;
  /** Supply it and the guardrail offers a one-click switch to the borrower shape. */
  onShapeChange?: (shape: ReportShape) => void;
  disabled?: boolean;
  /** Fixed clock, for tests and for a dry-run preview pinned to a resolve time. */
  now?: Date;
  className?: string;
}

export function WindowPicker({
  value,
  onChange,
  shape,
  onShapeChange,
  disabled = false,
  now,
  className = '',
}: WindowPickerProps) {
  const clock = now ?? new Date();
  const today = istToday(clock);
  const resolved = resolveWindow(value, clock);
  const error = windowError(value, shape, clock);
  const overCap = shape === 'detailed' && resolved.days > DETAILED_MAX_WINDOW_DAYS;
  const atCap = shape === 'detailed' && resolved.days === DETAILED_MAX_WINDOW_DAYS;
  const typeHint = WINDOW_TYPES.find((entry) => entry.value === value.type)?.hint ?? '';
  // `custom` is two fixed dates; every other type is resolved again against the clock.
  // The sentence below is only true of the relative ones, and it used to render on all
  // seven, twice the height of the fact it was explaining.
  const isRelative = value.type !== 'custom';

  return (
    <div className={`stack ${className}`.trim()}>
      {/* Explicit tracks, packed left: `.select` is width:100% by design inside a field,
          so an auto-fit row handed a seven-option dropdown the full 1,006px of an xl
          dialog. */}
      <div
        className="field-row"
        style={{
          gridTemplateColumns:
            'minmax(260px, 420px) minmax(150px, 210px) minmax(150px, 210px)',
          justifyContent: 'start',
        }}
      >
        <Field
          label="Window"
          hint={
            isRelative
              ? `${typeHint}${typeHint ? '. ' : ''}The dispatcher resolves the window again in IST at fire time, so a relative window moves with the clock.`
              : typeHint
          }
        >
          {(control) => (
            <Select
              {...control}
              value={value.type}
              disabled={disabled}
              onChange={(event) =>
                onChange(defaultWindowFor(event.target.value as WindowType, value))
              }
            >
              {WINDOW_TYPES.map((entry) => (
                <option key={entry.value} value={entry.value}>
                  {entry.label}
                </option>
              ))}
            </Select>
          )}
        </Field>

        {value.type === 'last_n_days' ? (
          <Field label="Days" hint="Inclusive, ending yesterday (IST)" required>
            {(control) => (
              <Input
                {...control}
                type="number"
                min={1}
                max={MAX_LAST_N_DAYS}
                step={1}
                mono
                disabled={disabled}
                value={Number.isFinite(value.n) && value.n > 0 ? String(value.n) : ''}
                onChange={(event) => {
                  const parsed = Number.parseInt(event.target.value, 10);
                  onChange({ type: 'last_n_days', n: Number.isNaN(parsed) ? 0 : parsed });
                }}
              />
            )}
          </Field>
        ) : null}

        {value.type === 'from_date' ? (
          <Field label="From (IST)" hint="Runs from this date through today" required>
            {(control) => (
              <Input
                {...control}
                type="date"
                max={today}
                mono
                disabled={disabled}
                value={value.from}
                onChange={(event) => onChange({ type: 'from_date', from: event.target.value })}
              />
            )}
          </Field>
        ) : null}

        {value.type === 'custom' ? (
          <>
            <Field label="From (IST)" required>
              {(control) => (
                <Input
                  {...control}
                  type="date"
                  max={today}
                  mono
                  disabled={disabled}
                  value={value.from}
                  onChange={(event) =>
                    onChange({ type: 'custom', from: event.target.value, to: value.to })
                  }
                />
              )}
            </Field>
            <Field label="To (IST)" required>
              {(control) => (
                <Input
                  {...control}
                  type="date"
                  max={today}
                  mono
                  disabled={disabled}
                  value={value.to}
                  onChange={(event) =>
                    onChange({ type: 'custom', from: value.from, to: event.target.value })
                  }
                />
              )}
            </Field>
          </>
        ) : null}
      </div>

      {/* A value read back to the operator is not an alert, so it gets no box. This used
          to be a tinted callout of exactly the same geometry as the §3 refusal beneath
          it, on the component whose whole job is refusing a run that would fail — three
          identically-shaped boxes told apart by hue alone. The resolved range is the
          answer and carries the weight; the day count is the number the guardrail turns
          on, so it is tabular and goes warn at the ceiling. */}
      <div className="readback">
        {/* `custom` IS the range, so it is never read back as resolving to itself. */}
        {isRelative ? (
          <span className="dim">{describeWindow(value)} resolves to </span>
        ) : null}
        <span className="strong">{istDateRange(resolved.from, resolved.to)}</span>
        <span className="dim"> · </span>
        <span
          className="num strong"
          style={atCap || overCap ? { color: 'var(--warn)' } : undefined}
        >
          {pluralise(resolved.days, 'day')}
        </span>
        <span className="dim"> of source data.</span>
      </div>

      {overCap ? (
        <div className="callout tone-danger" role="alert">
          <span className="callout-glyph" aria-hidden="true">
            ✕
          </span>
          <div className="stack-tight" style={{ minWidth: 0 }}>
            <div className="strong">
              A detailed report cannot span {resolved.days} days.
            </div>
            <div>{detailedGuardrailMessage(resolved.days)}</div>
            {onShapeChange ? (
              <div className="cluster">
                <Button size="sm" variant="primary" onClick={() => onShapeChange('borrower')}>
                  Switch to the borrower shape
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onChange({ type: 'last_n_days', n: DETAILED_MAX_WINDOW_DAYS })}
                >
                  Or shorten to {DETAILED_MAX_WINDOW_DAYS} days
                </Button>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* One day from a refusal is the state where an operator can still act cheaply, and
          it used to be the quietest treatment in the component while the refusal a day
          later shouted. Same warn vocabulary as the badges, one step below the refusal. */}
      {atCap && !overCap ? (
        <div className="small" style={{ color: 'var(--warn)' }}>
          <span className="mono strong" aria-hidden="true">
            ▲{' '}
          </span>
          At the {DETAILED_MAX_WINDOW_DAYS}-day ceiling for a detailed report. One more day and the
          dispatcher refuses the run.
        </div>
      ) : null}

      {error && !overCap ? (
        <div className="callout tone-danger" role="alert">
          <span className="callout-glyph" aria-hidden="true">
            ✕
          </span>
          <span>{error}</span>
        </div>
      ) : null}
    </div>
  );
}

export default WindowPicker;
